import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { ROLES, MODULES, MODULE_LABELS, ROLE_LABELS } from "../../context/AuthContext";

const CreateAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    countryCode: "",
    contactNumber: "",
    fullContactNumber: "",
    role: ROLES.SUB_ADMIN,
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const allModules = Object.values(MODULES);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePhoneChange = (value, country) => {
    const dialCode = country?.dialCode ? `+${country.dialCode}` : "";
    const rawNumber = value.replace(country?.dialCode || "", "").trim();

    setFormData((prev) => ({
      ...prev,
      countryCode: dialCode,
      contactNumber: rawNumber,
      fullContactNumber: dialCode + rawNumber,
    }));
    if (errors.contactNumber) {
      setErrors((prev) => ({ ...prev, contactNumber: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length > 50) {
      newErrors.name = "Name must be less than 50 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.role) {
      newErrors.role = "Role is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePermissionChange = (module) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(module)) {
        return prev.filter((p) => p !== module);
      } else {
        return [...prev, module];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPermissions.length === allModules.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions([...allModules]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Validate permissions for sub admin role
    if (formData.role === ROLES.SUB_ADMIN && selectedPermissions.length === 0) {
      toast.error("Please select at least one module permission for Sub Admin");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
      };

      // Add permissions only for sub admin role
      if (formData.role === ROLES.SUB_ADMIN) {
        payload.permissions = selectedPermissions;
      }

      if (formData.countryCode && formData.contactNumber) {
        payload.countryCode = formData.countryCode;
        payload.contactNumber = formData.contactNumber;
      }

      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/create`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success("Admin created successfully");
      navigate("/admin-management");
    } catch (error) {
      console.error("Error creating admin:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.response?.message ||
        "Failed to create admin";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Create Admin</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/admin-management">Admin Management</Link>
                </li>
                <li className="breadcrumb-item active">Create Admin</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-8">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">New Admin Details</h3>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="card-body">
                    <div className="form-group">
                      <label htmlFor="name">
                        Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                        id="name"
                        name="name"
                        placeholder="Enter name"
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                      {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${errors.email ? "is-invalid" : ""}`}
                        id="email"
                        name="email"
                        placeholder="Enter email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                      {errors.email && (
                        <div className="invalid-feedback">{errors.email}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="password">
                        Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type={showPassword ? "text" : "password"}
                          className={`form-control ${errors.password ? "is-invalid" : ""}`}
                          id="password"
                          name="password"
                          placeholder="Enter password"
                          value={formData.password}
                          onChange={handleInputChange}
                        />
                        <div className="input-group-append">
                          <span
                            className="input-group-text"
                            style={{ cursor: "pointer" }}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                          </span>
                        </div>
                        {errors.password && (
                          <div className="invalid-feedback">{errors.password}</div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword">
                        Confirm Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                          id="confirmPassword"
                          name="confirmPassword"
                          placeholder="Confirm password"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                        />
                        <div className="input-group-append">
                          <span
                            className="input-group-text"
                            style={{ cursor: "pointer" }}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            <i className={showConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                          </span>
                        </div>
                        {errors.confirmPassword && (
                          <div className="invalid-feedback">{errors.confirmPassword}</div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="contactNumber">Contact Number</label>
                      <PhoneInput
                        country={"us"}
                        value={formData.fullContactNumber}
                        onChange={handlePhoneChange}
                        inputClass="form-control"
                        containerClass="phone-input-container"
                        inputStyle={{ width: "100%" }}
                      />
                      {errors.contactNumber && (
                        <div className="text-danger small">{errors.contactNumber}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="role">
                        Role <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-control ${errors.role ? "is-invalid" : ""}`}
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                      >
                        <option value={ROLES.SUB_ADMIN}>Sub Admin - Custom module access</option>
                        <option value={ROLES.SUPER_ADMIN}>Super Admin - Full access + admin management</option>
                      </select>
                      {errors.role && (
                        <div className="invalid-feedback">{errors.role}</div>
                      )}
                      <small className="text-muted">
                        {formData.role === ROLES.SUPER_ADMIN 
                          ? "Super Admin has full access to all modules and can manage other admins."
                          : "Sub Admin will have access only to selected modules below."}
                      </small>
                    </div>

                    {/* Module Permissions - Only show for Sub Admin role */}
                    {formData.role === ROLES.SUB_ADMIN && (
                      <div className="form-group">
                        <label>
                          Module Permissions <span className="text-danger">*</span>
                        </label>
                        <div className="mb-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleSelectAll}
                          >
                            {selectedPermissions.length === allModules.length ? "Deselect All" : "Select All"}
                          </button>
                          <span className="ml-2 text-muted">
                            ({selectedPermissions.length} of {allModules.length} selected)
                          </span>
                        </div>
                        <div className="row">
                          {allModules.map((module) => (
                            <div className="col-md-6" key={module}>
                              <div className="form-check mb-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`module-${module}`}
                                  checked={selectedPermissions.includes(module)}
                                  onChange={() => handlePermissionChange(module)}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`module-${module}`}
                                >
                                  {MODULE_LABELS[module]}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="card-footer">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2"></span>
                          Creating...
                        </>
                      ) : (
                        "Create Admin"
                      )}
                    </button>
                    <Link to="/admin-management" className="btn btn-secondary ml-2">
                      Cancel
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreateAdmin;
